import React, { FC, useEffect, useState } from 'react';
import { Form } from 'rsuite';
import { RsInput } from 'components/Rsuite/Input';
import { RsButton } from 'components/Rsuite/Button';
import { Uploader } from 'rsuite';
import { useCampaignStore } from 'store/campaignStore';
import { useProductStore } from 'store/productStore';
import { useFormik } from 'formik';
import WarningRoundIcon from '@rsuite/icons/WarningRound';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import {
  AWSS3Manager,
  CloudStorageManager,
} from 'core/cloudStorage/CloudStorageManager';
import { FileType } from 'rsuite/esm/Uploader';
import { useOrderStore } from 'store/orderStore';
import {
  CreativeManager,
  DefaultCreativeManager,
} from 'core/creative/CreativeManager';
import {
  BindingManager,
  DefaultBindingManager,
} from 'core/binding/BindingManager';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import { toast } from 'react-toastify';
import useCreativeStore from 'store/creativeStore';
import useCreativeForm from '../Hooks/useCreativeForm';
import useRichProductNativeFormValidation from 'components/Rsuite/CreativeForm/Hooks/useRichProductNativeFormValidation';
import { useCoreContext } from 'contexts/coreContext';
import _ from 'lodash';
import { numberWithCommas } from 'utils/StringUtil';

const cloudStorageManager: CloudStorageManager = new AWSS3Manager();
const creativeManager: CreativeManager = new DefaultCreativeManager();
const bindingManager: BindingManager = new DefaultBindingManager();

interface RichProductNativeFormProps {
  campaignId: string;
}

const RichProductNativeForm: FC<RichProductNativeFormProps> = ({
  campaignId,
}) => {
  const core = useCoreContext();
  const rawAgencyId = _.get(core, 'accountManager.localeMeta.agencyId');
  const agencyId: string | undefined =
    rawAgencyId !== undefined ? String(rawAgencyId) : undefined;
  const { order } = useOrderStore();
  const { openProductManager, handleLoading } = useCampaignStore();
  const { selectedProducts, changeSelectedProducts, resetProductStore } =
    useProductStore();
  const { actionType, creative, openCreativeManager, handleChangeCreative } =
    useCreativeStore();
  const { getCreativesByCampaignId, getCreative } = useCreativeForm();
  // 圖片檔案
  const [imgFile, setImgFile] = useState<FileType[]>([]);
  // 新增素材後端回傳的素材資料，後續要綁定活動需要素材ID
  const [newCreative, setNewCreative] = useState<any>();
  const [creativePreview, setCreativePreview] = useState<React.ReactNode>(null);
  // 根據actionType來定義初始值
  const initialValues =
    actionType === 'edit'
      ? {
          ...creative,
          typeProperties: {
            width: creative?.creativeValues?.w || 0,
            height: creative?.creativeValues?.h || 0,
          },
          updateImg: false,
        }
      : {
          name: '',
          bannerImageUrl: undefined,
          localImg: imgFile,
          fileSize: undefined,
          fileType: undefined,
          typeProperties: {
            width: undefined,
            height: undefined,
            imageUrl: '',
          },
        };
  // 初始化formik
  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      handleLoading(true);
      switch (actionType) {
        case 'create':
          handleUploadUrl();
          break;
        case 'edit':
          if (formik.values.updateImg) {
            handleUploadUrl();
          } else {
            handleUpdateCreative(formik.values.bannerImageUrl);
          }
          break;
        default:
          handleUploadUrl();
          break;
      }
    },
  });
  // 驗證表單
  const { errors, handleValidation } = useRichProductNativeFormValidation({
    defaultForm: creative,
    values: formik.values,
  });
  // 取得圖片尺寸
  const getImageDimensions = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function (event) {
        const img = new Image();
        img.onload = function () {
          resolve({ width: img.width, height: img.height, type: file.type });
        };
        img.onerror = function () {
          reject(new Error('無法載入圖片'));
        };

        if (event.target && typeof event.target.result === 'string') {
          img.src = event.target.result;
        }
      };

      reader.onerror = function () {
        reject(new Error('無法讀取檔案'));
      };

      reader.readAsDataURL(file);
    });
  };
  // 取得檔案大小
  const getFileSizeMB = (file) => {
    const fileSizeInBytes = file.size;
    const fileSizeInMB = fileSizeInBytes / (1024 * 1024);
    return fileSizeInMB.toFixed(2);
  };
  // 檢查上傳圖片
  const handleCheckUploadImg = async () => {
    try {
      if (imgFile && imgFile[0]?.blobFile) {
        const imageUrl = await getImageDimensions(imgFile[0].blobFile);
        const { width, height, type } = imageUrl as {
          width: string;
          height: string;
          type: string;
        };
        const fileSizeMB = getFileSizeMB(imgFile[0]?.blobFile);
        formik.setFieldValue('typeProperties.width', width);
        formik.setFieldValue('typeProperties.height', height);
        formik.setFieldValue('fileType', type);
        formik.setFieldValue('fileSize', Number(fileSizeMB));
        formik.setFieldValue('updateImg', true);
      }
      if (imgFile && imgFile[0]?.url) {
        const img = new Image();
        img.src = imgFile[0].url;
        img.onload = function () {
          formik.setFieldValue('typeProperties.width', img.width);
          formik.setFieldValue('typeProperties.height', img.height);
        };
      }
    } catch (error) {}
  };
  // 上傳圖片至S3
  const handleUploadUrl = async () => {
    try {
      if (imgFile && imgFile[0]?.blobFile) {
        const imageUrl = await cloudStorageManager.uploadFileToCloud(
          imgFile[0].blobFile,
        );
        if (imageUrl) {
          formik.setFieldValue('bannerImageUrl', imageUrl);
          if (actionType === 'edit') {
            handleUpdateCreative(imageUrl);
          } else {
            handleCreateCreative(imageUrl);
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };
  // 刪除已選商品
  const deleteProduct = (productId) => {
    changeSelectedProducts((products) =>
      products.filter((product) => product.productId !== productId),
    );
  };
  // 新增素材
  const handleCreateCreative = async (imageUrl) => {
    try {
      const creativeJson = selectedProducts.map((product) => ({
        creative: {
          name: formik.values.name,
          creativeType: 12,
          advertiserId: order.advertiserId,
          tenmaxCategory: 'e_commerce',
          typeProperties: {
            width: formik.values?.typeProperties.width.toString(),
            height: formik.values?.typeProperties.height.toString(),
            imageUrl: imageUrl,
            product: JSON.stringify(product),
          },
        },
      }));
      const data = await creativeManager.createCreativesByJson(creativeJson);
      if (data) {
        setNewCreative(data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };
  // 更新素材
  const handleUpdateCreative = async (imageUrl) => {
    try {
      const updateCrative = {
        ...formik.values,
        tenmaxCategory: 'e_commerce',
        typeProperties: {
          width: formik.values?.typeProperties.width.toString(),
          height: formik.values?.typeProperties.height.toString(),
          imageUrl: imageUrl,
          product: selectedProducts[0],
        },
      };
      const formData = new FormData();
      if (agencyId) {
        formData.append('creative.agencyId', agencyId as string);
      } else {
        throw new Error('agencyId is undefined');
      }
      formData.append('creative.creativeType', updateCrative.creativeType);
      formData.append('creative.name', updateCrative.name);
      formData.append('creative.tenmaxCategory', 'e_commerce');
      formData.append('creative.bannerExtra', updateCrative.bannerExtra);
      formData.append('creative.bannerImageUrl', updateCrative.bannerImageUrl);
      formData.append('creative.retail', updateCrative.retail);
      formData.append('creative.id', updateCrative.id);
      formData.append('creative.advertiserId', order.advertiserId.toString());
      Object.keys(updateCrative.typeProperties).forEach((key) => {
        if (key === 'product') {
          formData.append(
            `creative.typeProperties[${key}]`,
            JSON.stringify(updateCrative.typeProperties[key]),
          );
        } else {
          formData.append(
            `creative.typeProperties[${key}]`,
            updateCrative.typeProperties[key],
          );
        }
      });
      const data = await creativeManager.updateCreative(formData);
      if (data) {
        toast.success('素材更新成功');
        getCreativesByCampaignId(campaignId);
        openCreativeManager(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      handleLoading(false);
    }
  };
  // 綁定素材
  const handleBindCreative = async () => {
    try {
      const creativeIds = newCreative.map((creative) =>
        creative.creative.creativeId.toString(),
      );
      const bind = await bindingManager.createBinding(L1ObjectChannel.RTB, [
        {
          l2ChannelIds: [campaignId],
          active: true,
          creativeIds: creativeIds,
        },
      ]);
      console.log('bind', bind);
      toast.success('素材建立成功');
      getCreativesByCampaignId(campaignId);
      openCreativeManager(false);
    } catch (error) {
      console.error(error);
    } finally {
      handleLoading(false);
    }
  };
  // 生成素材預覽
  const setPrview = async () => {
    if (selectedProducts.length > 0) {
      try {
        if (actionType === 'create') {
          const src = imgFile[0].blobFile
            ? URL.createObjectURL(imgFile[0].blobFile)
            : '';
          switch (formik.values.typeProperties.width) {
            case 960:
              setCreativePreview(
                <div className='flex'>
                  <div
                    style={{
                      backgroundImage: `url(${src})`,
                    }}
                    className='h-[125px] w-[375px] grid-cols-[auto_1fr] bg-[size:100%_100%] bg-no-repeat p-[15px]'
                  >
                    <div
                      className='relative float-right box-border grid h-[95px] w-[172px] grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-[5px] rounded bg-white p-[5px]'
                      style={{
                        gridTemplateAreas: `"goodsImage goodsTitle" "goodsImage goodsPrice"`,
                      }}
                    >
                      <div
                        className='size-[85px] rounded-[4px] bg-cover bg-center'
                        style={{
                          backgroundImage: `url(${selectedProducts[0]?.imgLink})`,
                          gridArea: 'goodsImage',
                        }}
                      ></div>
                      <div
                        className='line-clamp-3 h-[54px] break-words text-left text-[11px] leading-[16px] tracking-normal text-[#454545]'
                        style={{ gridArea: 'goodsTitle' }}
                      >
                        {selectedProducts[0]?.name}
                      </div>
                      <div>
                        <span className='inline-block h-[18px] w-[8px] text-left text-[13px] text-[#D62872]'>
                          $
                        </span>
                        <span className='inline-block text-left text-[17px] tracking-normal text-[#D62872]'>
                          {numberWithCommas(
                            Number(selectedProducts[0]?.salePrice).toFixed(0),
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>,
              );
              break;
            default:
              break;
          }
        } else {
          // 使用 fetch 發送請求
          if (!selectedProducts[0]?.adServingUrl) {
            throw new Error('adServingUrl is undefined');
          }
          const response = await fetch(selectedProducts[0].adServingUrl);
          // 檢查 response 是否成功 (status code 在 200-299 之間)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          // 將 response body 解析成 JSON
          const productInfo = await response.json();
          const src = !formik.values.updateImg
            ? imgFile[0].url
            : imgFile[0].blobFile
              ? URL.createObjectURL(imgFile[0].blobFile)
              : '';
          switch (formik.values.typeProperties.width) {
            case 960:
              setCreativePreview(
                <div className='flex'>
                  <div
                    style={{
                      backgroundImage: `url(${src})`,
                    }}
                    className='h-[125px] w-[375px] grid-cols-[auto_1fr] bg-[size:100%_100%] bg-no-repeat p-[15px]'
                  >
                    <div
                      className='relative float-right box-border grid h-[95px] w-[172px] grid-cols-[auto_1fr] grid-rows-[auto_auto] gap-x-[5px] rounded bg-white p-[5px]'
                      style={{
                        gridTemplateAreas: `"goodsImage goodsTitle" "goodsImage goodsPrice"`,
                      }}
                    >
                      <div
                        className='size-[85px] rounded-[4px] bg-cover bg-center'
                        style={{
                          backgroundImage: `url(${productInfo.image_link})`,
                          gridArea: 'goodsImage',
                        }}
                      ></div>
                      <div
                        className='line-clamp-3 h-[48px] break-words text-left text-[11px] leading-[16px] tracking-normal text-[#454545]'
                        style={{ gridArea: 'goodsTitle' }}
                      >
                        {productInfo.title}
                      </div>
                      <div>
                        <div className='text-left text-[17px] tracking-normal text-[#D62872]'>
                          <span className='inline-block'>$</span>
                          <span className='inline-block'>
                            {numberWithCommas(productInfo.sale_price)}
                          </span>
                        </div>
                        <div className='text-left text-[11px] tracking-normal text-[#B3B3B3]'>
                          <span className='inline-block line-through'>$</span>
                          <span className='inline-block line-through'>
                            {numberWithCommas(productInfo.price)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>,
              );
              break;
            default:
              break;
          }
        }
      } catch (error) {}
    }
  };
  // 編輯狀態需要重新取得 creative 資料
  useEffect(() => {
    if (actionType === 'edit' && creative) {
      getCreative(creative.id);
    }
  }, []);

  // component 卸載時
  // 1. 要清空正在編輯的 creative
  // 2. 清空 主打品(product), 因為其他素材類型也有 product
  useEffect(() => {
    return () => {
      formik.resetForm();
      resetProductStore();
      handleChangeCreative(null);
    };
  }, []);
  // 編輯狀態將creative圖片資料放入imgFile
  useEffect(() => {
    if (actionType === 'edit' && creative) {
      setImgFile([
        {
          name: creative.bannerImageUrl,
          url: creative.bannerImageUrl,
          fileKey: 1,
        },
      ]);
      changeSelectedProducts(() => [creative.product]);
    }
  }, [creative]);

  useEffect(() => {
    handleCheckUploadImg();
  }, [imgFile]);

  useEffect(() => {
    if (newCreative) {
      handleBindCreative();
    }
  }, [newCreative]);

  useEffect(() => {
    handleValidation();
  }, [formik.values]);

  useEffect(() => {
    setPrview();
  }, [
    selectedProducts,
    imgFile,
    formik.values.updateImg,
    formik.values.typeProperties.width,
  ]);
  return (
    <>
      <Form.Group controlId='name'>
        <Form.ControlLabel>素材名稱</Form.ControlLabel>
        <RsInput
          value={formik.values.name}
          onChange={(value) => formik.setFieldValue('name', value)}
          className='!w-[420px]'
          errorMessage={errors?.name as string}
        />
      </Form.Group>
      <Form.Group controlId='imageUrl'>
        <Form.ControlLabel>圖片檔案</Form.ControlLabel>
        <div className='rs-form-control-wrapper'>
          <Uploader
            listType='picture'
            autoUpload={false}
            multiple={false}
            accept='image/png'
            maxPreviewFileSize={2 * 1024 * 1024}
            fileList={imgFile}
            action=''
            draggable
            onChange={setImgFile}
            disabled={imgFile?.length > 0}
            onRemove={() => {
              formik.setFieldValue('bannerImageUrl', undefined);
              formik.setFieldValue('typeProperties.width', undefined);
              formik.setFieldValue('typeProperties.height', undefined);
            }}
            renderThumbnail={(file) => {
              const src =
                actionType === 'edit' && !formik.values.updateImg
                  ? file.url
                  : file.blobFile
                    ? URL.createObjectURL(file.blobFile)
                    : '';
              return (
                <>
                  <img
                    src={src}
                    alt=''
                    style={{ width: '100%', height: '100%' }}
                  />
                  <div className='flex flex-col gap-1'>
                    {(errors?.typeProperties?.width !== undefined ||
                      errors?.typeProperties?.height !== undefined) && (
                      <span className='inlin-block text-red-500'>{`不支援圖片尺寸${formik.values.typeProperties?.width} x ${formik.values.typeProperties?.height}`}</span>
                    )}
                    {errors?.fileType !== undefined && (
                      <span className='inlink-block text-red-500'>
                        {'檔案格式不符合PNG'}
                      </span>
                    )}
                    {errors?.fileSize !== undefined && (
                      <span className='inlink-block text-red-500'>
                        {'檔案大小須小於 2MB'}
                      </span>
                    )}
                  </div>
                </>
              );
            }}
            className='!flex flex-col-reverse'
          >
            {imgFile?.length === 0 ? (
              <div
                style={{
                  width: '420px',
                  height: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border:
                    formik?.errors?.bannerImageUrl !== undefined
                      ? '1px dashed #f44336'
                      : '1px dashed #d9d9d9',
                }}
              >
                <span>拖放檔案或選取</span>
              </div>
            ) : (
              <></>
            )}
          </Uploader>
          <div className='mt-2 flex flex-col gap-1'>
            <span className='inline-block text-[12px] font-medium leading-[1.17] text-[#98969d]'>
              限定 PNG 格式，圖片尺寸須符合 960 x 320
            </span>
            <span className='inline-block text-[12px] font-medium leading-[1.17] text-[#98969d]'>
              檔案大小須小於 2MB
            </span>
          </div>
        </div>
      </Form.Group>
      <Form.Group controlId='product'>
        <Form.ControlLabel>商品</Form.ControlLabel>
        {selectedProducts.length > 0 && actionType === 'create' && (
          <div className='flex items-start gap-2'>
            <div className='float-left flex max-h-[400px] w-[420px] flex-wrap gap-2 overflow-y-auto rounded border border-gray-300 px-2 py-3'>
              {selectedProducts.map((product) => (
                <span
                  key={product.productId}
                  className='inline-block break-words border border-[#98969d] bg-[#f7f7f7] p-2 text-[#242328]'
                >
                  {product.name}
                  <WarningRoundIcon
                    className='ml-1 cursor-pointer text-[#413993]'
                    onClick={() => deleteProduct(product.productId)}
                  />
                </span>
              ))}
            </div>
            <div>
              <IconWithTooltip
                icon={faPencilAlt}
                tooltipProps={{
                  id: `select-product-field-edit-products-hint`,
                  tooltip: i18n.t<string>(
                    'creativeSetupFlow.labels.editProductsHint',
                  ),
                }}
                onClick={() => openProductManager(true)}
              />
            </div>
          </div>
        )}
        {selectedProducts.length > 0 && actionType !== 'create' && (
          <div className='flex items-start gap-2'>
            <div className='float-left w-[420px] rounded'>
              {selectedProducts.map((product) => (
                <span
                  key={product.productId}
                  className='inline-block break-words'
                >
                  {product.name}
                </span>
              ))}
            </div>
          </div>
        )}
        {selectedProducts.length === 0 && (
          <RsButton
            appearance='ghost'
            className='!border-[#615f67] !text-[#615f67]'
            onClick={() => openProductManager(true)}
            errorMessage={selectedProducts.length === 0 ? '尚未選擇商品' : ''}
          >
            選擇商品
          </RsButton>
        )}
      </Form.Group>
      <Form.Group controlId='review'>
        <Form.ControlLabel>素材預覽</Form.ControlLabel>
        {creativePreview}
      </Form.Group>
      <div className='pb-4 pt-10'>
        <div className='flex items-center justify-start gap-4'>
          <RsButton
            appearance='primary'
            onClick={() => formik.handleSubmit()}
            disabled={
              actionType === 'create'
                ? !_.isEmpty(errors) || selectedProducts.length === 0
                : !_.isEmpty(errors)
            }
          >
            <span className='text-[16px]'>完成</span>
          </RsButton>
        </div>
      </div>
    </>
  );
};

export default RichProductNativeForm;
